import HistoryHelper from '@Services/HistoryHelper';
import UserDatabases from '@Services/UserDatabases';
import History from '@Services/History';
import get from 'lodash/get';
import map from 'lodash/map';
import each from 'lodash/each';
import filter from 'lodash/filter';
import find from 'lodash/find';
import sortBy from 'lodash/sortBy';

/**
 * @ngdoc function
 * @name getHistorySections
 * @returns {Promise} history sections
 * @description return array of history sections based on user's databases
 */
function getHistorySections() {
  const userDatabases = UserDatabases.getAll();

  return new Promise((resolve) => {
    resolve(
      map(userDatabases, (database) => {
        return {
          id: `history-${database.name}`,
          displayName: database.name,
          type: 'database',
          databaseId: database.id,
          childrenCount: 0,
          children: [],
        };
      })
    );
  });
}

/**
 * @ngdoc function
 * @name prepareHistoryItems
 * @param {array} historyItems collection of history items
 * @returns {array} history items
 * @description return collection of prepared history items
 */
function prepareHistoryItems(historyItems) {
  return map(historyItems, (historyItem) => {
    const structure = historyItem.structure || get(historyItem, 'quickSearchItem.structure');
    const structures = historyItem.queryFactItem ? HistoryHelper.extractStructuresFromFacts(historyItem.queryFactItem) : null;

    return {
      type: 'history',
      context: historyItem.context,
      query: historyItem.query,
      structures: structures || (structure ? [structure] : null),
      description: historyItem.description,
      totalResult: historyItem.resultSize,
      recordCount: historyItem.recordCount,
      resultName: historyItem.resultName,
      databaseId: historyItem.databaseId,
      queryFactItem: historyItem.queryFactItem,
      quickSearchItem: historyItem.quickSearchItem,
    };
  });
}

/**
 * @ngdoc function
 * @name updateHistorySections
 * @param {array} historySections collection of history sections
 * @param {array} historyItems collection of history items
 * @param {string} sectionType history type ('saved' or 'recent')
 * @description updates each history section with history items
 */
function updateHistorySections(historySections, historyItems, sectionType) {
  const isSavedItems = sectionType === 'saved';
  const displayName = isSavedItems ? 'Saved' : 'Recent';

  each(historySections, (section) => {
    const filteredHistoryItems = filter(historyItems, (historyItem) => {
      return section.databaseId === historyItem.databaseId;
    });

    if (filteredHistoryItems.length) {
      section.children.push({
        id: `history-${section.displayName}-${sectionType}`,
        displayName,
        type: 'section',
        childrenCount: filteredHistoryItems.length,
        children: filteredHistoryItems,
        isSavedHistorySection: isSavedItems,
      });

      // eslint-disable-next-line no-param-reassign
      section.childrenCount = section.children.length;
    }
  });
}

/**
 * @ngdoc function
 * @name addSavedHistory
 * @param {array} historySections collection of history sections
 * @returns {Promise} history sections
 * @description return collection of history sections populated with saved history
 */
function addSavedHistory(historySections) {
  return History.getSavedHistory()
    .then((rawHistoryItems) => {
      const preparedHistoryItems = prepareHistoryItems(rawHistoryItems);

      if (preparedHistoryItems.length) {
        updateHistorySections(historySections, preparedHistoryItems, 'saved');
      }

      return historySections;
    })
    .catch(Logger.reject);
}

/**
 * @ngdoc function
 * @name addRecentHistory
 * @param {array} historySections collection of history sections
 * @returns {array} history sections
 * @description return collection of history sections populated with recent history
 */
function addRecentHistory(historySections) {
  const preparedHistoryItems = prepareHistoryItems(History.retrieveRecentHistory());

  preparedHistoryItems.reverse();

  if (preparedHistoryItems.length) {
    updateHistorySections(historySections, preparedHistoryItems, 'recent');
  }

  return historySections;
}

/**
 * @ngdoc function
 * @name removeEmptySections
 * @param {array} historySections collection of history sections
 * @returns {array} filtered history sections
 * @description removing history sections without recent or saved history items
 */
function removeEmptySections(historySections) {
  return filter(historySections, (section) => {
    return section.childrenCount > 0;
  });
}

/**
 * @ngdoc function
 * @name sortHistorySections
 * @param {array} historySections collection of history sections
 * @returns {array} sorted history sections
 * @description sorting history sections in order to acceptance criteria of RXFE-3115
 * "Reaxys always comes first then the remaining database in alphabetical order."
 */
function sortHistorySections(historySections) {
  const reaxysDB = UserDatabases.getDefaultDatabase();
  if (historySections.length < 2) {
    return historySections;
  }

  const reaxysSection = find(historySections, (section) => {
    return reaxysDB.id === section.databaseId;
  });

  const filteredHistorySections = filter(historySections, (section) => {
    return reaxysDB.id !== section.databaseId;
  });

  const sortedHistorySections = sortBy(filteredHistorySections, (section) => {
    return section.displayName.toLowerCase();
  });

  if (sortedHistorySections && reaxysSection) {
    sortedHistorySections.unshift(reaxysSection);
  }

  return sortedHistorySections;
}

/**
 * @ngdoc function
 * @name getStructuredHistory
 * @returns {Promise} structured history items
 * @description return data for "History" tab on Query Builder
 */
const getStructuredHistory = () => {
  return getHistorySections()
    .then(addRecentHistory)
    .then(addSavedHistory)
    .then(removeEmptySections)
    .then(sortHistorySections)
    .catch(Logger.reject);
};

export default { getStructuredHistory };
